import {Box, Button, Flex, Group} from '@mantine/core';
import React, {useCallback} from 'react';

import * as AnalysisActions from 'analysis-tool/AnalysisActions';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PreviewOutput, PreviewStepParams} from 'pipelines/network/PipelineDAO';
import {PipelineStep} from 'pipelines/PipelineTypes';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {
  getMostRecentData,
  NOT_FETCHED_ASYNC_STATE,
  AsyncStatus,
  AsyncState,
} from 'ui-library/hooks/useAsyncState';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {toPreviewStepDatas} from './LiveStepDataPreview';
import PresentationalStepDataPreview, {StepDataRole} from './PresentationalStepDataPreview';

const previewGlobalState = makeUseGlobalClientState<AsyncState<PreviewOutput>>(NOT_FETCHED_ASYNC_STATE);

export interface ManualStepDataPreviewProps {
  draftStep: PipelineStep;
  clientStateId: string;
}

/**
 * Wrapper for `<PresentationalStepDataPreview>`, which allows the user to manually generate preview outputs.
 *
 * Manually-triggered previews are used for steps whose previews are too expensive to generate automatically.
 *
 * The preview is only fetched when the user clicks the generate/regenerate/retry button.
 *
 * @package
 */
export default function ManualStepDataPreview({
  draftStep,
  clientStateId,
}: ManualStepDataPreviewProps): JSX.Element {
  const [previewState, setPreviewState] = previewGlobalState.useGlobalClientState(clientStateId);

  const generatePreview = useCallback((): void => {
    if (!draftStep.config) {
      return;
    }
    const requestData: PreviewStepParams<PipelineStepType> = {
      stepType: draftStep.stepType,
      stepConfig: draftStep.config,
      stepInput: Object.fromEntries(
        draftStep.inputStepDatas.map((inputStepData) => [
          inputStepData.name,
          inputStepData.rawDatas.map(({id}) => id),
        ])
      ),
    };
    AnalysisActions.runPreviewAnalysisStep(requestData, {status: AsyncStatus.NOT_FETCHED}, setPreviewState);
  }, [draftStep, setPreviewState]);

  const previewOutput = getMostRecentData(previewState);

  const previewStepDatas = previewOutput
    ? toPreviewStepDatas(previewOutput, draftStep.stepType, draftStep.stepVersion)
    : null;

  if (previewState.status === AsyncStatus.LOADING) {
    return <CenteredHorizontalThrobber />;
  }

  if (previewState.status === AsyncStatus.ERROR) {
    return (
      <Flex direction="column" h="100%" py="sm" sx={{overflowY: 'auto'}}>
        <Box mx="md" my="sm">
          <InlineBanner bannerType={InlineBannerType.ERROR} text="A preview could not be generated." />
        </Box>
        <Group position="center" mx="md" my="sm">
          <Button variant="secondary" onClick={generatePreview}>
            Retry Preview
          </Button>
        </Group>
      </Flex>
    );
  }

  if (previewState.status === AsyncStatus.SUCCESS) {
    return (
      <Flex direction="column" h="100%" py="sm" sx={{overflowY: 'auto'}}>
        <PresentationalStepDataPreview
          stepType={draftStep.stepType}
          stepVersion={draftStep.stepVersion}
          stepDataRole={StepDataRole.OUTPUT}
          clientStateId={clientStateId}
          preview={previewStepDatas ?? {}}
        />
        <Group position="center" mx="md" my="sm">
          <Button variant="secondary" onClick={generatePreview}>
            Regenerate Preview
          </Button>
        </Group>
      </Flex>
    );
  }

  return (
    <Flex direction="column" h="100%" py="sm" sx={{overflowY: 'auto'}}>
      <Box mx="md" my="sm">
        <InlineBanner
          bannerType={InlineBannerType.INFO}
          text="Click the button below to generate a preview. This may take a moment."
        />
      </Box>
      <Flex align="center" justify="center" mx="md" my="sm" sx={{flex: 1}}>
        <Button variant="primary" onClick={generatePreview}>
          Generate Preview
        </Button>
      </Flex>
    </Flex>
  );
}
